import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableProps } from '../Table';
import { useAdjustTableHandlers } from '../hooks/useAdjustTableHandlers';
import { UseColumnConfigResult } from '../hooks/useColumnConfig';
import { useScrollSync } from '../hooks/useScrollSync';
import { useTableInternalState } from '../hooks/useTableInternalState';
import { isStickyLeft, isStickyRight } from '../utils/columnLayout';
import { MIN_COLUMNS_FOR_ADJUST_TABLE } from '../utils/constants';
import {
  getColumnConfigDialogProps,
  getOrderedAdjustTableColumns,
  getStickyOffsetStyles,
  getStickyWidth,
  getTableClasses,
} from './TableInternal.helpers';
import { TableFooterControls, TableScrollableSection, TableToolbarSection } from './TableInternalSections';

interface TableInternalProps<T> extends Omit<TableProps<T>, 'id'> {
  /** Always resolved by `Table` via `id ?? _uniqueId('table')`. */
  id: string;
  adjustTableColumns?: TableProps<T>['columns'];
  enableColumnConfig?: boolean;
  columnConfig?: UseColumnConfigResult;
  isColumnConfigLoading?: boolean;
}

const TableInternal = <T,>({
  columns,
  adjustTableColumns,
  data,
  filter,
  className,
  options,
  id,
  isLoading,
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder,
  downloadButtonProps,
  toolbarElement,
  headerElement,
  filterSlot,
  filterSlotWidth,
  preTableSlot,
  showToolbarBorder = false,
  enableColumnResizing = false,
  enableColumnConfig = false,
  columnConfig,
  isColumnConfigLoading = false,
  externalInteractionSignal,
  additionalActionBarElement,
  'data-testid': dataTestId = 'table-internal',
}: TableInternalProps<T>) => {
  const { t } = useTranslation();
  const {
    table,
    tableWrapperRef,
    tableHeight,
    containsStickyColumns,
    positionedColumnDefs,
    columnOffsets,
    toggleRow,
    rowExpandedDelayed,
    grouping,
    pagination,
    totalCount,
    visibleColumnCount,
    orderedColumns,
    isAdjustTableOpen,
    setIsAdjustTableOpen,
    setColumnOrder,
    columnIdKey,
    columnOrder,
    additionalHeaderLabels,
    resetColumnState,
    hasHorizontalOverflow,
  } = useTableInternalState({
    columns,
    data,
    filter,
    options,
    id,
    isLoading,
    enableColumnResizing,
    externalInteractionSignal,
  });

  const orderedAdjustTableColumns = useMemo(
    () => getOrderedAdjustTableColumns(adjustTableColumns, columnOrder),
    [adjustTableColumns, columnOrder],
  );

  const { handleAdjustTableClick, handleAdjustTableClose, handleAdjustTableApply, handleAdjustTableReset } =
    useAdjustTableHandlers({
      id,
      columnIdKey,
      columnConfig,
      setColumnOrder,
      resetColumnState,
      setIsAdjustTableOpen,
    });

  const { topScrollbarRef, topScrollbarInnerRef, showTopScrollbar, handleKeyDown } = useScrollSync({
    tableWrapperRef,
    hasHorizontalOverflow,
  });

  const tableClasses = getTableClasses({
    className,
    scrollMode: options?.scrollMode,
    hasHorizontalOverflow,
    containsStickyColumns,
  });

  const stickyLeftWidth = useMemo(() => {
    return getStickyWidth(containsStickyColumns, positionedColumnDefs, isStickyLeft);
  }, [containsStickyColumns, positionedColumnDefs]);

  const stickyRightWidth = useMemo(() => {
    return getStickyWidth(containsStickyColumns, positionedColumnDefs, isStickyRight);
  }, [containsStickyColumns, positionedColumnDefs]);

  const columnConfigDialogProps = useMemo(
    () => getColumnConfigDialogProps(columnConfig, isColumnConfigLoading),
    [columnConfig, isColumnConfigLoading],
  );

  const { stickyLeftOffsetStyle, stickyRightOffsetStyle, stickyHorizontalOffsetStyle } = getStickyOffsetStyles(
    stickyLeftWidth,
    stickyRightWidth,
  );

  const shouldShowAdjustTable = enableColumnConfig && columns.length >= MIN_COLUMNS_FOR_ADJUST_TABLE;

  return (
    <>
      <TableToolbarSection
        downloadButtonProps={downloadButtonProps}
        showToolbarBorder={showToolbarBorder}
        shouldShowAdjustTable={shouldShowAdjustTable}
        onAdjustTableClick={handleAdjustTableClick}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchClear={onSearchClear}
        searchPlaceholder={searchPlaceholder}
        toolbarElement={toolbarElement}
        headerElement={headerElement}
        filterSlot={filterSlot}
        filterSlotWidth={filterSlotWidth}
        additionalActionBarElement={additionalActionBarElement}
      />
      {preTableSlot}
      <TableScrollableSection
        showTopScrollbar={showTopScrollbar}
        topScrollbarRef={topScrollbarRef}
        topScrollbarInnerRef={topScrollbarInnerRef}
        tableWrapperRef={tableWrapperRef}
        tableClasses={tableClasses}
        tableHeight={tableHeight}
        hasHorizontalOverflow={hasHorizontalOverflow}
        handleKeyDown={handleKeyDown}
        scrollableTableAreaLabel={t('global.table.scrollableTableArea')}
        hasGrouping={!!grouping.length}
        containsStickyColumns={containsStickyColumns}
        dataTestId={dataTestId}
        table={table}
        options={options}
        positionedColumnDefs={positionedColumnDefs}
        columnOffsets={columnOffsets}
        tableId={id}
        isLoading={!!isLoading}
        onToggleRow={toggleRow}
        rowExpandedDelayed={rowExpandedDelayed}
        visibleColumnCount={visibleColumnCount}
        stickyHorizontalOffsetStyle={stickyHorizontalOffsetStyle}
        stickyLeftOffsetStyle={stickyLeftOffsetStyle}
        stickyRightOffsetStyle={stickyRightOffsetStyle}
        searchHighlightValue={searchValue ?? filter}
      />
      <TableFooterControls
        isLoading={isLoading}
        options={options}
        table={table}
        pagination={pagination}
        totalCount={totalCount}
        tableId={id}
        shouldShowAdjustTable={shouldShowAdjustTable}
        isAdjustTableOpen={isAdjustTableOpen}
        handleAdjustTableClose={handleAdjustTableClose}
        orderedAdjustTableColumns={orderedAdjustTableColumns}
        orderedColumns={orderedColumns}
        handleAdjustTableApply={handleAdjustTableApply}
        handleAdjustTableReset={handleAdjustTableReset}
        additionalHeaderLabels={additionalHeaderLabels}
        columnConfigDialogProps={columnConfigDialogProps}
      />
    </>
  );
};

export default TableInternal;
